import { z } from 'zod';

/**
 * Structured description of a technical rider as extracted by an LLM (decision U9). The model
 * only EXTRACTS; matching and merging into a board is deterministic (`planAiMerge`).
 *
 * Positions are approximate and normalised to the drawn stage plot: `x` 0 = left edge of the
 * plot as drawn (stage right when the audience is at the bottom), 1 = right edge; `y` 0 =
 * upstage (back), 1 = downstage (audience edge). Every field except the identifying ones is
 * optional – riders are informal (decision Q11).
 */

// Stryker disable StringLiteral: `.describe()` texts only guide the LLM (JSON schema hints).
/** Normalised position on the stage plot (0..1 each, clamped when merged). */
export const AiPosition = z.object({
  x: z.number().describe('0 = left edge of the drawn plot, 1 = right edge'),
  y: z.number().describe('0 = upstage/back, 1 = downstage/audience edge'),
});
export type AiPosition = z.infer<typeof AiPosition>;

const text = z.string();
const count = z.number().int().positive();
const confidence = z.number().describe('0..1 how sure the reading is');

export const AiStage = z.object({
  width: z.number().positive().optional().describe('Stage width (left-right)'),
  depth: z.number().positive().optional().describe('Stage depth (front-back)'),
  units: z.enum(['m', 'ft']).optional(),
});
export type AiStage = z.infer<typeof AiStage>;

export const AiMusician = z.object({
  name: text.describe('Name or role as written, e.g. "Trp1", "Lead Vocals", "Magnus"'),
  instrument: text.optional(),
  position: AiPosition.optional(),
  confidence: confidence.optional(),
});
export type AiMusician = z.infer<typeof AiMusician>;

export const AiInstrument = z.object({
  name: text.describe('e.g. "Drumset", "Grand Piano", "Bass amp"'),
  model: text.optional(),
  musician: text.optional(),
  quantity: count.optional(),
  position: AiPosition.optional(),
  notes: text.optional(),
});
export type AiInstrument = z.infer<typeof AiInstrument>;

export const AiMicrophone = z.object({
  purpose: text.describe('What it picks up, e.g. "Snare", "Moderation", "Solo 1"'),
  model: text.optional().describe('Model text as written, alternatives allowed ("MD421, SM57")'),
  channel: count.optional().describe('Input channel number if known'),
  stand: text.optional().describe('Stand text as written ("High (small Boom)", "Clip")'),
  musician: text.optional(),
  phantom: z.boolean().optional(),
  quantity: count.optional(),
  position: AiPosition.optional(),
});
export type AiMicrophone = z.infer<typeof AiMicrophone>;

export const AiDi = z.object({
  purpose: text,
  model: text.optional(),
  channel: count.optional(),
  musician: text.optional(),
  position: AiPosition.optional(),
});
export type AiDi = z.infer<typeof AiDi>;

export const AI_MONITOR_TYPES = ['wedge', 'sidefill', 'iem', 'drumfill'] as const;
export const AiMonitor = z.object({
  type: z.enum(AI_MONITOR_TYPES),
  model: text.optional(),
  mix: text.optional().describe('Name or number of the mix feeding it'),
  musician: text.optional(),
  quantity: count.optional(),
  position: AiPosition.optional(),
});
export type AiMonitor = z.infer<typeof AiMonitor>;

export const AiMix = z.object({
  number: count.optional(),
  name: text,
  kind: z.enum(['monitor', 'iem', 'foh', 'fx']).optional(),
  stereo: z.boolean().optional(),
  outputs: z.array(text).optional().describe('Monitors fed by this mix'),
  sends: z.array(text).optional().describe('Channel names or numbers wanted in this mix'),
});
export type AiMix = z.infer<typeof AiMix>;

export const AiChannel = z.object({
  number: count,
  name: text,
  source: text.optional().describe('Mic/DI text as written ("MD421, SM57", "DI-Out")'),
  micModel: text.optional(),
  stand: text.optional(),
  phantom: z.boolean().optional(),
  insert: text.optional(),
  patch: text.optional(),
  musician: text.optional(),
  notes: text.optional(),
});
export type AiChannel = z.infer<typeof AiChannel>;

export const AiStagebox = z.object({
  name: text,
  model: text.optional(),
  inputs: count.optional(),
  outputs: count.optional(),
  position: AiPosition.optional(),
});
export type AiStagebox = z.infer<typeof AiStagebox>;

export const AiPower = z.object({
  voltage: z.number().positive().optional(),
  plug: text.optional().describe('e.g. "Schuko", "CEE 16A", "CEE 32A 3-phase"'),
  count: count.optional().describe('Number of outlets, "3x 230 V" → 3'),
  position: AiPosition.optional(),
  notes: text.optional(),
});
export type AiPower = z.infer<typeof AiPower>;

export const AiRiser = z.object({
  label: text.optional(),
  w: z.number().positive().optional(),
  d: z.number().positive().optional(),
  h: z.number().positive().optional(),
  position: AiPosition.optional(),
});
export type AiRiser = z.infer<typeof AiRiser>;

export const AiExtraction = z.object({
  stage: AiStage.optional(),
  musicians: z.array(AiMusician).default([]),
  instruments: z.array(AiInstrument).default([]),
  microphones: z.array(AiMicrophone).default([]),
  dis: z.array(AiDi).default([]),
  monitors: z.array(AiMonitor).default([]),
  mixes: z.array(AiMix).default([]),
  channels: z.array(AiChannel).default([]),
  stageboxes: z.array(AiStagebox).default([]),
  power: z.array(AiPower).default([]),
  risers: z.array(AiRiser).default([]),
  notes: z.array(text).default([]),
  warnings: z.array(text).default([]),
});
export type AiExtraction = z.infer<typeof AiExtraction>;
export type AiExtractionInput = z.input<typeof AiExtraction>;

/** One line of a material/equipment list (decision U11), e.g. "4 × Shure SM57". */
export const AiMaterialLine = z.object({
  category: text.optional().describe('Section heading as written, e.g. "Technik", "Backline"'),
  name: text.describe('Article as written, e.g. "Shure SM57 Mikrofon"'),
  qty: z.number().nonnegative().optional().describe('Quantity; handwritten corrections win'),
  notes: text.optional().describe('Remarks incl. handwritten ones ("1x Piano", "nicht benötigt")'),
});
export type AiMaterialLine = z.infer<typeof AiMaterialLine>;

/** Recognised material list (second extraction mode `kind: 'materials'`). */
export const AiMaterialExtraction = z.object({
  lines: z.array(AiMaterialLine).default([]),
  warnings: z.array(text).default([]),
});
export type AiMaterialExtraction = z.infer<typeof AiMaterialExtraction>;

/** What an import session recognises: a rider (board data) or a material list. */
export const AI_IMPORT_KINDS = ['rider', 'materials'] as const;
export const AiImportKind = z.enum(AI_IMPORT_KINDS);
export type AiImportKind = z.infer<typeof AiImportKind>;
// Stryker restore StringLiteral

/** An empty extraction (every list empty). */
export function emptyExtraction(): AiExtraction {
  return AiExtraction.parse({});
}

/** Recursively drops `null` values (LLMs often send `null` for "unknown"). */
export function stripNulls(value: unknown): unknown {
  if (Array.isArray(value)) return value.filter((v) => v !== null).map(stripNulls);
  if (typeof value === 'object' && value !== null) {
    return Object.fromEntries(
      Object.entries(value)
        .filter(([, v]) => v !== null)
        .map(([k, v]) => [k, stripNulls(v)]),
    );
  }
  return value;
}

export type Parsed<T> = { ok: true; value: T } | { ok: false; issues: string[] };

function parseWith<T>(schema: z.ZodType<T>, raw: unknown): Parsed<T> {
  const parsed = schema.safeParse(stripNulls(raw));
  if (parsed.success) return { ok: true, value: parsed.data };
  return {
    ok: false,
    issues: parsed.error.issues.map((i) => `${i.path.join('.') || '(root)'}: ${i.message}`),
  };
}

/** Validates raw model output of a material list (nulls tolerated). */
export function parseAiMaterialExtraction(raw: unknown): Parsed<AiMaterialExtraction> {
  return parseWith(AiMaterialExtraction, raw);
}

export type ParsedExtraction =
  { ok: true; extraction: AiExtraction } | { ok: false; issues: string[] };

/** Validates raw model output (nulls tolerated) against {@link AiExtraction}. */
export function parseAiExtraction(raw: unknown): ParsedExtraction {
  const r = parseWith(AiExtraction, raw);
  return r.ok ? { ok: true, extraction: r.value } : r;
}

/** Keywords many structured-output implementations reject; ranges are enforced by zod/clamping. */
const UNSUPPORTED_KEYWORDS = [
  '$schema',
  'default',
  'minimum',
  'maximum',
  'exclusiveMinimum',
  'exclusiveMaximum',
];

function tidySchema(node: unknown): unknown {
  if (Array.isArray(node)) return node.map(tidySchema);
  if (typeof node !== 'object' || node === null) return node;
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(node)) {
    if (!UNSUPPORTED_KEYWORDS.includes(k)) out[k] = tidySchema(v);
  }
  if (out.type === 'object') out.additionalProperties = false;
  return out;
}

/**
 * JSON schema of {@link AiExtraction} for structured LLM output (`response_format`): closed
 * objects, no numeric ranges or defaults.
 */
export function aiExtractionJsonSchema(kind: AiImportKind = 'rider'): Record<string, unknown> {
  const schema = kind === 'materials' ? AiMaterialExtraction : AiExtraction;
  return tidySchema(z.toJSONSchema(schema, { target: 'draft-7', io: 'input' })) as Record<
    string,
    unknown
  >;
}
